/**
 * ErrorBoundary.jsx — catches render/runtime crashes in the subtree so a single
 * broken component shows a readable error instead of black-screening the whole app.
 * The exact message + component stack are displayed (and logged) so bugs are
 * diagnosable in production without DevTools.
 */
import React from 'react';
import { tr } from '@quantum/ui';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught:', error, info?.componentStack);
    this.setState({ info });
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const msg = error?.message || String(error);
    const stack = (info?.componentStack || error?.stack || '').toString();

    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999, overflow: 'auto',
        background: 'radial-gradient(ellipse at top, #161b25 0%, #0b0e14 70%)',
        color: '#e6ecf6', fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 16, padding: 28, textAlign: 'center',
      }}>
        <div style={{ fontSize: 20, fontWeight: 600, color: '#FF5C87' }}>{tr('Something crashed')}</div>
        <div style={{
          fontFamily: '"JetBrains Mono", monospace', fontSize: 12.5, color: '#ffd1d6',
          maxWidth: 760, lineHeight: 1.55, background: 'rgba(255,92,135,0.08)',
          border: '1px solid rgba(255,92,135,0.30)', borderRadius: 10, padding: '12px 14px',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>{msg}</div>
        {stack && (
          <pre style={{
            fontFamily: '"JetBrains Mono", monospace', fontSize: 10.5, color: 'rgba(210,220,236,0.6)',
            maxWidth: 760, maxHeight: 280, overflow: 'auto', textAlign: 'left',
            background: 'rgba(20,24,34,0.6)', border: '1px solid rgba(180,200,230,0.10)',
            borderRadius: 10, padding: '12px 14px', width: '100%',
          }}>{stack.trim()}</pre>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => this.setState({ error: null, info: null })} style={{
            padding: '9px 18px', borderRadius: 9, cursor: 'pointer', color: '#2CF5A7',
            background: 'linear-gradient(180deg, rgba(44,245,167,0.22), rgba(44,245,167,0.06))',
            border: '1px solid rgba(44,245,167,0.40)', fontWeight: 600, fontSize: 12.5,
          }}>{tr('Try again')}</button>
          <button onClick={() => window.location.reload()} style={{
            padding: '9px 18px', borderRadius: 9, cursor: 'pointer', color: 'rgba(210,220,236,0.7)',
            background: 'rgba(180,200,230,0.05)', border: '1px solid rgba(180,200,230,0.12)', fontSize: 12.5,
          }}>{tr('Reload')}</button>
        </div>
      </div>
    );
  }
}
